/**
 * Rental Service
 *
 * Rental contracts and condition reports (états des lieux) management.
 */

import apiClient, {
  APIResponse,
  PaginatedResponse,
  unwrapResponse,
  unwrapPaginatedResponse,
} from './apiClient';
import { ENDPOINTS, PAGINATION } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export type ContractStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'VALIDATED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED' | 'DEFAULTED';
export type ConditionReportType = 'ENTREE' | 'SORTIE';

export interface RentalContract {
  id: string;
  code: string;
  client_id: string;
  client_nom?: string;
  machine_id: string;
  machine_nom?: string;
  machine_reference?: string;
  created_by_id: string;
  created_by_nom?: string;
  date_debut: string;
  date_fin_prevue: string;
  date_fin_reelle?: string;
  tarif_journalier: number;
  montant_caution: number;
  caution_payee: boolean;
  caution_restituee: boolean;
  montant_total_prevu: number;
  status: ContractStatus;
  etat_lieux_entree_id?: string;
  etat_lieux_sortie_id?: string;
  notes?: string;
  created_at: string;
  updated_at: string;

  // Computed
  duree_jours: number;
  has_entry_report: boolean;
  has_exit_report: boolean;
}

export interface RentalContractCreate {
  client_id: string;
  machine_id: string;
  date_debut: string;
  date_fin_prevue: string;
  tarif_journalier?: number; // Uses machine's default if not specified
  montant_caution?: number;
  notes?: string;
}

export interface RentalContractUpdate {
  date_fin_prevue?: string;
  tarif_journalier?: number;
  notes?: string;
  conditions_particulieres?: string;
}

export interface ChecklistItem {
  key: string;
  label: string;
  checked: boolean;
  comment?: string;
}

export interface ConditionReport {
  id: string;
  code: string;
  contrat_location_id: string;
  contrat_code?: string;
  type_etat: ConditionReportType;
  date_etat: string;
  releve_compteur?: number;
  niveau_carburant?: number;
  checklist: ChecklistItem[];
  photos: string[]; // URLs
  signature_client?: string;
  signature_agent?: string;
  observations?: string;
  effectue_par_id: string;
  effectue_par_nom?: string;
  created_at: string;
}

export interface ConditionReportPhoto {
  url: string;
  description?: string;
  ordre: number;
}

export interface ConditionReportCreate {
  type_etat: ConditionReportType;
  date_etat?: string;
  releve_compteur?: number;
  niveau_carburant?: string;
  checklist: Record<string, boolean>;
  observations?: string;
  dommages_constates?: string;
  signature_client: string;
  signature_agent: string;
  nom_client?: string;
  nom_agent?: string;
  photos: ConditionReportPhoto[];
}

export interface ConditionReportFinalize {
  signature_client: string;
  signature_agent: string;
}

export interface MachineAvailability {
  machine_id: string;
  machine_designation: string;
  date: string;
  is_available: boolean;
  contract_id?: string;
  contract_code?: string;
}

// =============================================================================
// List Params
// =============================================================================

export interface RentalListParams {
  page?: number;
  page_size?: number;
  client_id?: string;
  product_id?: string;
  status?: ContractStatus;
  date_from?: string;
  date_to?: string;
  active_only?: boolean;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

// =============================================================================
// Rental Service
// =============================================================================

export const rentalService = {
  // ---------------------------------------------------------------------------
  // Contracts
  // ---------------------------------------------------------------------------

  async listContracts(params: RentalListParams = {}) {
    const { page = PAGINATION.DEFAULT_PAGE, page_size = PAGINATION.DEFAULT_PAGE_SIZE, ...filters } = params;
    const response = await apiClient.get<PaginatedResponse<RentalContract>>(ENDPOINTS.LOCATIONS, {
      params: { page, page_size, ...filters },
    });
    return unwrapPaginatedResponse(response);
  },

  async getContract(id: string): Promise<RentalContract> {
    const response = await apiClient.get<APIResponse<RentalContract>>(`${ENDPOINTS.LOCATIONS}/${id}`);
    return unwrapResponse(response);
  },

  async createContract(data: RentalContractCreate): Promise<RentalContract> {
    const response = await apiClient.post<APIResponse<RentalContract>>(ENDPOINTS.LOCATIONS, data);
    return unwrapResponse(response);
  },

  async updateContract(id: string, data: RentalContractUpdate): Promise<RentalContract> {
    const response = await apiClient.patch<APIResponse<RentalContract>>(`${ENDPOINTS.LOCATIONS}/${id}`, data);
    return unwrapResponse(response);
  },

  async submitForApproval(id: string): Promise<RentalContract> {
    const response = await apiClient.post<APIResponse<RentalContract>>(`${ENDPOINTS.LOCATIONS}/${id}/soumettre`);
    return unwrapResponse(response);
  },

  async validateContract(id: string): Promise<RentalContract> {
    const response = await apiClient.post<APIResponse<RentalContract>>(`${ENDPOINTS.LOCATIONS}/${id}/valider`);
    return unwrapResponse(response);
  },

  async approveContract(id: string, siteStockId?: string): Promise<RentalContract> {
    const config = siteStockId ? { params: { site_stock_id: siteStockId } } : undefined;
    const response = await apiClient.post<APIResponse<RentalContract>>(
      `${ENDPOINTS.LOCATIONS}/${id}/approuver`,
      undefined,
      config,
    );
    return unwrapResponse(response);
  },

  async listPendingApproval() {
    return this.listContracts({ status: 'PENDING_APPROVAL' as ContractStatus });
  },

  async activateContract(id: string): Promise<RentalContract> {
    const response = await apiClient.post<APIResponse<RentalContract>>(`${ENDPOINTS.LOCATIONS}/${id}/activer`);
    return unwrapResponse(response);
  },

  async terminateContract(id: string, data: {
    date_fin_reelle?: string;
    montant_caution_retenu?: number;
    motif_retenue?: string;
    supplements?: number;
    motif_supplements?: string;
    notes_cloture?: string;
  }): Promise<RentalContract> {
    const response = await apiClient.post<APIResponse<RentalContract>>(`${ENDPOINTS.LOCATIONS}/${id}/terminer`, data);
    return unwrapResponse(response);
  },

  async cancelContract(id: string, reason: string): Promise<RentalContract> {
    const response = await apiClient.post<APIResponse<RentalContract>>(`${ENDPOINTS.LOCATIONS}/${id}/annuler`, { reason });
    return unwrapResponse(response);
  },

  async markDepositPaid(id: string, data: { mode_paiement: string; reference?: string }): Promise<RentalContract> {
    const response = await apiClient.post<APIResponse<RentalContract>>(`${ENDPOINTS.LOCATIONS}/${id}/caution-payee`, data);
    return unwrapResponse(response);
  },

  async refundDeposit(id: string, data: { montant: number; mode_paiement: string; reference?: string; notes?: string; signature_client: string }): Promise<RentalContract> {
    const response = await apiClient.post<APIResponse<RentalContract>>(`${ENDPOINTS.LOCATIONS}/${id}/rembourser-caution`, data);
    return unwrapResponse(response);
  },

  async getContractPdf(id: string): Promise<Blob> {
    const response = await apiClient.get(`${ENDPOINTS.LOCATIONS}/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getDepositReceiptPdf(id: string): Promise<Blob> {
    const response = await apiClient.get(`${ENDPOINTS.LOCATIONS}/${id}/recu-caution`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getRefundReceiptPdf(id: string): Promise<Blob> {
    const response = await apiClient.get(`${ENDPOINTS.LOCATIONS}/${id}/recu-remboursement`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async listPendingDepositRefunds(): Promise<RentalContract[]> {
    const response = await apiClient.get<APIResponse<RentalContract[]>>(`${ENDPOINTS.LOCATIONS}/cautions/en-attente-remboursement`);
    return unwrapResponse(response);
  },

  // ---------------------------------------------------------------------------
  // Condition Reports (États des lieux)
  // ---------------------------------------------------------------------------

  async getConditionReports(contractId: string): Promise<ConditionReport[]> {
    const response = await apiClient.get<APIResponse<ConditionReport[]>>(
      `${ENDPOINTS.LOCATIONS}/${contractId}/etats-des-lieux`
    );
    return unwrapResponse(response);
  },

  async getConditionReport(contractId: string): Promise<ConditionReport[]> {
    // Uses the etats-des-lieux endpoint for a contract
    return this.getConditionReports(contractId);
  },

  async createConditionReport(contractId: string, data: ConditionReportCreate): Promise<ConditionReport> {
    // Route to correct endpoint based on type
    const endpoint = data.type_etat === 'SORTIE' ? 'retour' : 'depart';
    // Remove type_etat before sending — not part of backend schema
    const { type_etat, ...payload } = data as any;
    const response = await apiClient.post<APIResponse<ConditionReport>>(
      `${ENDPOINTS.LOCATIONS}/${contractId}/${endpoint}`,
      payload
    );
    return unwrapResponse(response);
  },

  async addPhotoToReport(_reportId: string, _photoUrl: string): Promise<ConditionReport> {
    // Photos are included in the depart/retour requests
    throw new Error('Photos are uploaded as part of depart/retour operations');
  },

  async finalizeConditionReport(_id: string, _data: ConditionReportFinalize): Promise<ConditionReport> {
    // Condition reports are finalized as part of depart/retour
    throw new Error('Condition reports are finalized during depart/retour operations');
  },

  async getConditionReportPdf(_id: string): Promise<Blob> {
    // No standalone EDL PDF endpoint - use contract PDF instead
    throw new Error('Use getContractPdf for contract documents');
  },

  // ---------------------------------------------------------------------------
  // Availability
  // ---------------------------------------------------------------------------

  async checkAvailability(_machineId: string, _startDate: string, _endDate: string): Promise<boolean> {
    // No dedicated availability endpoint - check via active contracts
    // Return true as a fallback; actual conflict detection is done server-side on creation
    return true;
  },

  async getCalendar(_machineId: string, _month: string): Promise<MachineAvailability[]> {
    // No dedicated calendar endpoint
    return [];
  },

  async getActiveContracts(): Promise<RentalContract[]> {
    const response = await apiClient.get<PaginatedResponse<RentalContract>>(`${ENDPOINTS.LOCATIONS}/active`, {
      params: { page: 1, page_size: 100 },
    });
    return unwrapPaginatedResponse(response).data;
  },

  // ---------------------------------------------------------------------------
  // Checklist Template
  // ---------------------------------------------------------------------------

  async getChecklistTemplate(): Promise<Omit<ChecklistItem, 'checked' | 'comment'>[]> {
    // Default checklist template (no backend endpoint)
    return [
      { key: 'etat_general', label: 'Etat general de la machine' },
      { key: 'carrosserie', label: 'Carrosserie et peinture' },
      { key: 'moteur', label: 'Moteur et transmission' },
      { key: 'pneus', label: 'Pneus et roues' },
      { key: 'eclairage', label: 'Eclairage et signalisation' },
      { key: 'hydraulique', label: 'Systeme hydraulique' },
      { key: 'outils', label: 'Outils et accessoires' },
      { key: 'documents', label: 'Documents de bord' },
    ];
  },
};

export default rentalService;
